/**
 * Mixin for filtering payment methods based on cart items, product restrictions,
 * and user context. This mixin handles the complex logic of determining which
 * payment methods are available for a given checkout scenario.
 *
 * Behavior summary:
 * - Start from backend-provided methods for the current legal entity.
 * - Reduce to methods allowed by each item (product, service, parent product),
 *   with special handling for payment items and exclude_from_all.
 * - Add saved Adyen cards as separate options and remove legacy/disabled methods
 *   (Stripe, Apple Pay).
 * - Apply item-type rules for value cards, gift cards, and periodic cards,
 *   including value card constraints when combined with other methods.
 * - Apply product restrictions by intersecting allowed methods across items,
 *   while always keeping valuecard/giftcard available.
 * - Sort by priority for display.
 *
 * Required data properties in component:
 * - methods: Array of payment method objects (filtered by backend based on payment_ids if provided)
 * - computedItems: Array of items in the cart
 * - totalFromServer: Number representing the total price
 * - valueCardAmount: Number representing value card balance
 * - valueCardCannotBeUsed: Boolean
 * - valueCardRemoved: Boolean
 * - invoiceValueCard: Boolean
 *
 * Required computed properties:
 * - itemsContainValueCardOrGiftCard: Boolean
 * - itemsContainPeriodicCards: Boolean
 *
 * Required getters:
 * - effectiveLegalEntityId: Effective legal entity ID (override or selected)
 *
 * Required methods:
 * - getMethodName(method): Method to get translated method name
 *
 * Provides computed properties:
 * - commonMethods: Base filtered methods from cart items
 * - methodsWithSavedCards: Methods with saved card options added
 * - methodsFilteredByItemType: Methods filtered by cart item types
 * - itemsWithProductRestrictions: Items that have product restrictions
 * - filteredMethods: Final filtered methods based on all criteria
 * - sortedMethods: Filtered methods sorted by priority
 *
 * Provides methods:
 * - applyProductRestrictions(methods, items): Apply product restrictions to methods
 */

export default {
    computed: {
        /**
         * Base filtered methods from cart items
         */
        commonMethods() {
            let methods = this.methods.concat();
            this.computedItems.forEach(item => {
                if (item.product && item.product.methods && item.product.methods.length) {
                    methods = methods.filter(m => item.product.methods.includes(m.id));
                } else if (item.parentProductMethods && item.parentProductMethods.length) {
                    methods = methods.filter(m => item.parentProductMethods.includes(m.id));
                } else if (item.service && item.service.methods && item.service.methods.length) {
                    methods = methods.filter(m => item.service.methods.includes(m.id));
                } else if (item.payment) {
                    let direct = methods.filter(m => ['stripe', 'swishi', 'epassi'].includes(m.id));

                    // Remove excluded methods if not specifically included
                    direct = direct.filter(m => m.exclude_from_all ? item.payments?.every(p => p.method == m.id) : true);

                    methods = methods.filter(m => direct.includes(m) || m.id.startsWith('adyen-'));
                } else {
                    if (!['valuecard', 'giftcard', 'discount'].includes(item.type)) {
                        methods = methods.filter(m => !m.exclude_from_all);
                    }
                }
            })
            return methods;
        },

        /**
         * Filter out valuecard and add saved card options if available
         */
        methodsWithSavedCards() {
            let methods = this.commonMethods.filter(method => method.id !== 'valuecard');

            let cardMethod = methods.find(x => x.id == 'adyen-card');
            if (cardMethod) {
                methods = methods.filter(x => x.id !== 'stripe');
                this.$store.state.user.adyen?.saved_cards?.forEach((card, i) => {
                    // Only show a saved card that can actually be charged at
                    // checkout. The backend validates the card against the
                    // cart's legal entity, and a card without a legal_entity_id
                    // (or one belonging to a different entity) is always
                    // rejected with "No such saved card" — so showing it just
                    // gives the user a dead option.
                    if (!card.legal_entity_id || !this.effectiveLegalEntityId || card.legal_entity_id != this.effectiveLegalEntityId) return;
                    methods.unshift({
                        ...cardMethod,
                        id: 'adyen-card:saved:' + i,
                        name: this.$translate('Saved payment card'),
                        subtitle: card.info,
                        imageSrc: cardMethod.imageSrc,
                        ref: card.ref
                    });
                })
            }

            // Include Apple Pay only on IPad and Iphones
            methods = methods.filter(x => x.id != 'adyen-applepay' || window.ApplePaySession);

            // Disable Apple Pay for now. Remove the following row to enable.
            methods = methods.filter(x => x.id != 'adyen-applepay');

            methods = methods.filter(x => x.id !== 'stripe');  // Stripe is not used anymore

            return methods;
        },

        /**
         * Filter methods based on item types (value cards, gift cards, periodic cards)
         */
        methodsFilteredByItemType() {
            let methods = this.methodsWithSavedCards;

            if (this.itemsContainValueCardOrGiftCard) {
                methods = methods.filter(method => method.id.startsWith('adyen-') || ['stripe', 'stripe_saved', 'swishi'].includes(method.id));
            } else {
                if (!isNaN(this.totalFromServer) && this.totalFromServer > 0 && !this.valueCardCannotBeUsed && !this.valueCardRemoved && this.$store.state.user && this.valueCardAmount > 0) {
                    // For value cards, remove Autogiro and Unknown and possibly Invoice
                    let not_allowed = ['directdebit', 'unknown'];
                    if (!this.invoiceValueCard) {
                        not_allowed.push('invoice');
                    }
                    methods = methods.filter(m => !not_allowed.includes(m.id));
                }
            }

            if (this.itemsContainPeriodicCards) {
                methods = methods.filter(method => method.periodic);
            }

            return methods;
        },

        /**
         * Get all items that have product restrictions.
         */
        itemsWithProductRestrictions() {
            const items = [];

            // Add invoice items (products in the cart)
            this.computedItems.forEach(item => {
                if (item.product || item.service || item.parentProductMethods) {
                    items.push(item);
                }
            });

            return items;
        },

        /**
         * Final filtered methods based on all criteria including product restrictions
         */
        filteredMethods() {
            if (!this.computedItems.length || !this.$store.state.user) {
                return [];
            }

            let methods = this.methodsFilteredByItemType;

            // Apply product restrictions
            const itemsToCheck = this.itemsWithProductRestrictions;
            if (itemsToCheck.length > 0) {
                methods = this.applyProductRestrictions(methods, itemsToCheck);
            }

            return methods;
        },

        /**
         * Filtered methods sorted by priority
         */
        sortedMethods() {
            const leid = this.effectiveLegalEntityId;
            const hasConsent = leid && this.$store.state?.user?.consents
                ? !!this.$store.state.user.consents[leid]
                : this.$store.state?.user?.hasConsent();

            const METHOD_PRIORITIES = {
                'swishi': 90,
                'adyen-card': 85,
                'adyen-trustly': 85,
                'adyen-klarna_paynow': 80,
                'adyen-klarna': 80,
                'adyen-klarna_account': 80,
                'directdebit': 70,
                'invoice': 60,
                'unknown': 50
            };

            const SAVED_CARD_PRIORITY = 100;
            const DIRECTDEBIT_WITH_CONSENT_PRIORITY = 1000;

            const getPriority = (methodId) => {
                if (methodId === 'directdebit' && hasConsent) {
                    return DIRECTDEBIT_WITH_CONSENT_PRIORITY;
                }
                if (methodId.startsWith('adyen-card:saved')) {
                    return SAVED_CARD_PRIORITY;
                }
                if (METHOD_PRIORITIES[methodId] !== undefined) {
                    return METHOD_PRIORITIES[methodId];
                }
                return 0;
            };

            return [...this.filteredMethods].sort((a, b) => {
                const priorityDiff = getPriority(b.id) - getPriority(a.id);
                if (priorityDiff !== 0) {
                    return priorityDiff;
                }
                return this.getMethodName(a).localeCompare(this.getMethodName(b));
            });
        }
    },

    methods: {
        /**
         * Apply product restrictions to filter payment methods.
         * Calculates the intersection of allowed methods across all items.
         *
         * @param {Array} methods - Available payment methods
         * @param {Array} items - Items with product restrictions
         * @returns {Array} Filtered payment methods
         */
        applyProductRestrictions(methods, items) {
            const availableMethods = new Set(methods.map(m => m.id));
            let allowedMethods = null;

            items.forEach(item => {
                let itemMethods = null;

                if (item.product?.methods && item.product.methods.length > 0) {
                    itemMethods = new Set(item.product.methods.filter(m => availableMethods.has(m)));
                } else if (item.parentProductMethods && item.parentProductMethods.length > 0) {
                    itemMethods = new Set(item.parentProductMethods.filter(m => availableMethods.has(m)));
                } else if (item.service?.methods && item.service.methods.length > 0) {
                    itemMethods = new Set(item.service.methods.filter(m => availableMethods.has(m)));
                }

                // Only apply intersection if this item has restrictions
                if (itemMethods !== null) {
                    if (allowedMethods === null) {
                        allowedMethods = itemMethods;
                    } else {
                        allowedMethods = new Set([...allowedMethods].filter(m => itemMethods.has(m)));
                    }
                }
            });

            if (allowedMethods) {
                allowedMethods.add('valuecard');
                allowedMethods.add('giftcard');

                const allowedMethodsList = Array.from(allowedMethods);
                // A saved card (id 'adyen-card:saved:N') is the same payment method as
                // 'adyen-card' (Card payment Internet), so it must survive a product
                // restriction whenever adyen-card is allowed — otherwise selecting only
                // "Card payment Internet" for a product would hide the saved card.
                return methods.filter(method => {
                    const baseId = method.id.startsWith('adyen-card:saved') ? 'adyen-card' : method.id;
                    return allowedMethodsList.includes(baseId);
                });
            }

            return methods;
        }
    }
}
